import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import media from "../../styles/media";
import Container from "../ui/Container";
import { ButtonLink } from "../ui/Button";
import Spinner from "../ui/Spinner";
import StatusMessage from "../ui/StatusMessage";
import { fetchNewsList } from "../../api/news";
import useApiResource from "../../hooks/useApiResource";
import useLanguage from "../../hooks/useLanguage";
import { NEWS_PLACEHOLDER_IMAGE } from "../news/newsImage";
import { ROUTES } from "../../config/routes";

const Wrapper = styled.section`
  padding: 64px 0;
  background-color: ${({ theme }) => theme.colors.surfaceAlt};

  ${media.md} {
    padding: 40px 0;
  }
`;

const Heading = styled.h2`
  margin-bottom: 28px;
  font-size: ${({ theme }) => theme.fontSizes.h3};
  font-weight: 700;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  ${media.lg} {
    grid-template-columns: 1fr;
  }
`;

const Featured = styled(Link)`
  position: relative;
  display: flex;
  min-height: 460px;
  align-items: flex-end;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.primary};
  /*
   * Второй слой — запасной: если картинка новости не загрузилась, браузер
   * просто не отрисует верхний слой и покажет заглушку под ним.
   */
  background-image: ${({ $image }) =>
    $image
      ? `url("${$image}"), url("${NEWS_PLACEHOLDER_IMAGE}")`
      : `url("${NEWS_PLACEHOLDER_IMAGE}")`};
  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.onPrimary};

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.15) 60%,
      rgba(0, 0, 0, 0.05) 100%
    );
  }

  &:hover {
    text-decoration: none;
  }

  ${media.md} {
    min-height: 280px;
  }
`;

const FeaturedTitle = styled.h3`
  position: relative;
  z-index: 1;
  padding: 24px;
  font-size: ${({ theme }) => theme.fontSizes.h3};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.onPrimary};

  ${media.md} {
    padding: 16px;
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SideItem = styled(Link)`
  display: flex;
  min-height: 100px;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 18px 20px;
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: box-shadow ${({ theme }) => theme.transitions.base};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    text-decoration: none;
  }
`;

const SideTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 500;
`;

const SideMore = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const FEATURED_COUNT = 1;
const SIDE_COUNT = 4;

export function NewsSection() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: news, loading } = useApiResource(
    (signal) =>
      fetchNewsList({
        lang: language,
        limit: FEATURED_COUNT + SIDE_COUNT,
        signal,
      }),
    [language],
    { initialData: [] },
  );

  const [featured, ...rest] = news;
  const sideItems = rest.slice(0, SIDE_COUNT);

  return (
    <Wrapper>
      <Container>
        <Heading>{t("newsTitle2")}</Heading>

        {loading && <Spinner label={t("loading")} />}

        {!loading && news.length === 0 && (
          <StatusMessage title={t("empty_list")} />
        )}

        {!loading && featured && (
          <Grid>
            <Featured to={ROUTES.newsItem(featured.id)} $image={featured.image}>
              <FeaturedTitle>{featured.title}</FeaturedTitle>
            </Featured>

            <SideColumn>
              {sideItems.map((item) => (
                <SideItem key={item.id} to={ROUTES.newsItem(item.id)}>
                  <SideTitle>{item.title}</SideTitle>
                  <SideMore>{t("readMore2")}</SideMore>
                </SideItem>
              ))}

              <div>
                <ButtonLink to={ROUTES.news}>{t("allNews")}</ButtonLink>
              </div>
            </SideColumn>
          </Grid>
        )}
      </Container>
    </Wrapper>
  );
}

export default NewsSection;
